import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenantColors } from '../contexts/TenantBrandingContext';
import { HelpIcon } from '../components/HelpIcon';
import { TooltipProvider } from '../components/ui/tooltip';
import { getHelpContent } from '../lib/helpContent';
import { toast } from 'sonner';

interface Invite {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  created_at: string;
  expires_at: string;
  message?: string;
}

interface Member {
  id: string;
  user_id: string;
  tenant_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

interface InviteFormData {
  email: string;
  role: string;
  message: string;
}

const USER_ROLES = [
  { value: 'user', label: 'User', description: 'Can view and manage campaigns, contacts, and feedback' },
  { value: 'admin', label: 'Admin', description: 'Can manage all features including team settings and billing' },
  { value: 'owner', label: 'Owner', description: 'Full access including tenant management and deletion' },
];

export const TeamManagementPage: React.FC = () => {
  const { user, tenant } = useAuth();
  const { primary } = useTenantColors();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [revokingInvite, setRevokingInvite] = useState<string | null>(null);
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);
  const [formData, setFormData] = useState<InviteFormData>({
    email: '',
    role: 'user',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<InviteFormData>>({});

  // Check if team management feature is available
  const { data: featuresData } = useQuery({
    queryKey: ['billing-features'],
    queryFn: async () => {
      const response = await api.billing.getFeatures();
      return response.data;
    },
  });

  const hasTeamManagement = featuresData?.features?.team_management || false;

  useEffect(() => {
    if (hasTeamManagement) {
      fetchInvites();
      if (tenant?.id) {
        fetchMembers();
      }
      
      // Auto-refresh every 30 seconds to catch new acceptances
      const interval = setInterval(() => {
        fetchInvites();
        if (tenant?.id) {
          fetchMembers();
        }
      }, 30000);
      
      return () => clearInterval(interval);
    } else {
      setLoading(false);
      setMembersLoading(false);
    }
  }, [tenant?.id, hasTeamManagement]);

  const fetchInvites = async () => {
    try {
      const response = await api.invitations.list();
      const data = response.data;
      // Ensure we always set an array
      setInvites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    if (!tenant?.id) {
      setMembersLoading(false);
      return;
    }
    
    try {
      const response = await api.tenant.getMembers(tenant.id);
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
      setMembers([]); // Set empty array on error
    } finally {
      setMembersLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<InviteFormData> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await api.invitations.create(formData);
      setFormData({ email: '', role: 'user', message: '' });
      setShowInviteForm(false);
      setErrors({});
      await fetchInvites();
      toast.success(`Invitation sent to ${formData.email}`);
    } catch (error: any) {
      console.error('Error creating invite:', error);
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.detail || 'Failed to create invitation';
        setErrors({ email: errorMessage });
        toast.error(errorMessage);
      } else {
        toast.error('Failed to send invitation. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    const invite = invites.find(inv => inv.id === inviteId);
    const inviteEmail = invite?.email || 'this invitation';
    
    if (!window.confirm(`Are you sure you want to revoke the invitation to ${inviteEmail}?`)) {
      return;
    }
    
    setRevokingInvite(inviteId);
    try {
      await api.invitations.revoke(inviteId);
      await fetchInvites();
      toast.success('Invitation revoked successfully');
    } catch (error: any) {
      console.error('Error revoking invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke invitation');
    } finally {
      setRevokingInvite(null);
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    const invite = invites.find(inv => inv.id === inviteId);
    const inviteEmail = invite?.email || 'this invitation';
    
    setResendingInvite(inviteId);
    try {
      await api.invitations.resend(inviteId);
      toast.success(`Invitation resent to ${inviteEmail}`);
    } catch (error: any) {
      console.error('Error resending invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleUpdateRole = async (memberId: string, userId: string, newRole: string, oldRole: string) => {
    if (!tenant?.id) return;
    
    const member = members.find(m => m.user_id === userId);
    const memberName = member ? `${member.user.first_name} ${member.user.last_name}` : 'member';
    
    // Special confirmation for owner role changes
    if (newRole === 'owner' || oldRole === 'owner') {
      const action = newRole === 'owner' ? 'promote to owner' : 'remove owner role from';
      if (!window.confirm(`Are you sure you want to ${action} ${memberName}? This is a critical action.`)) {
        // Revert the select to previous value
        await fetchMembers();
        return;
      }
    }
    
    setUpdatingRole(userId);
    try {
      await api.tenant.updateMemberRole(tenant.id, userId, newRole);
      await fetchMembers();
      toast.success(`${memberName}'s role updated to ${newRole}`);
    } catch (error: any) {
      console.error('Error updating role:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update role';
      toast.error(errorMessage);
      // Revert the select to previous value on error
      await fetchMembers();
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleRemoveMember = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Are you sure you want to remove ${userEmail} from the team?`)) {
      return;
    }
    
    if (!tenant?.id) return;
    
    setRemovingMember(userId);
    try {
      await api.tenant.removeMember(tenant.id, userId);
      await fetchMembers();
      toast.success(`${userEmail} has been removed from the team`);
    } catch (error: any) {
      console.error('Error removing member:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to remove member';
      toast.error(errorMessage);
    } finally {
      setRemovingMember(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof InviteFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'revoked':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading || !featuresData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const teamManagementHelp = getHelpContent('teamManagement');

  // Show upgrade prompt if team management is not available
  if (!hasTeamManagement) {
    return (
      <TooltipProvider>
        <div className="space-y-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Team Management
              </h2>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <p className="text-gray-700 mb-4">
                <strong>Team Management</strong> is available on <strong>Growth+</strong> plans.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Upgrade to invite team members, manage roles, and collaborate with your team.
              </p>
              <a
                href="/billing"
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Upgrade to Growth Plan
              </a>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Team Management
              </h2>
              {teamManagementHelp && (
                <HelpIcon
                  tooltip="Learn about team management"
                  modalTitle={teamManagementHelp.title}
                  modalContent={teamManagementHelp.content}
                  size="md"
                />
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Manage team members and invitations for {tenant?.name}
            </p>
          </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={() => setShowInviteForm(true)}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            style={{ backgroundColor: primary }}
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Invite Team Member
          </button>
        </div>
      </div>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Invite Team Member</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full px-3 py-2 border ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    placeholder="colleague@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {USER_ROLES.map((role) => {
                      // Only owners can invite as owner
                      const currentMember = members.find(m => m.user_id === user?.id);
                      const isCurrentUserOwner = currentMember?.role === 'owner';
                      if (role.value === 'owner' && !isCurrentUserOwner) {
                        return null;
                      }
                      return (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {USER_ROLES.find(r => r.value === formData.role)?.description}
                  </p>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Add a personal message to the invitation..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteForm(false);
                      setFormData({ email: '', role: 'user', message: '' });
                      setErrors({});
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: primary }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* Current Members */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Current Team Members</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Manage roles and access for your team members
          </p>
        </div>
        
        {membersLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">No team members found.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {members.map((member) => (
              <li key={member.id}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {member.user.first_name.charAt(0)}{member.user.last_name.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">
                              {member.user.first_name} {member.user.last_name}
                              {user?.id === member.user_id && (
                                <span className="ml-2 text-xs text-gray-500">(You)</span>
                              )}
                            </p>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              member.role === 'owner' 
                                ? 'bg-purple-100 text-purple-800' 
                                : member.role === 'admin'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{member.user.email}</p>
                        </div>
                        <div className="flex items-center space-x-4">
                          {(() => {
                            // Get current user's membership to check role
                            const currentMember = members.find(m => m.user_id === user?.id);
                            const isCurrentUserOwner = currentMember?.role === 'owner';
                            const isCurrentUserAdmin = currentMember?.role === 'admin' || currentMember?.role === 'owner';
                            
                            // Disable if: it's yourself, OR it's an owner role (only owners can change owner roles)
                            // Since this page requires admin access, all users here are admins/owners
                            const shouldDisable = 
                              user?.id === member.user_id || 
                              (member.role === 'owner' && !isCurrentUserOwner) ||
                              !isCurrentUserAdmin;
                            
                            const isUpdating = updatingRole === member.user_id;
                            
                            return (
                              <select
                                value={member.role}
                                onChange={(e) => handleUpdateRole(member.id, member.user_id, e.target.value, member.role)}
                                disabled={shouldDisable || isUpdating}
                                className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              >
                            {USER_ROLES.map((role) => {
                              // Only owners can see/select the owner role
                              if (role.value === 'owner' && !isCurrentUserOwner) {
                                return null;
                              }
                              
                              return (
                                <option key={role.value} value={role.value}>
                                  {role.label}
                                </option>
                              );
                            })}
                              </select>
                            );
                          })()}
                          {user?.id !== member.user_id && member.role !== 'owner' && (
                            <button
                              onClick={() => handleRemoveMember(member.user_id, member.user.email)}
                              disabled={removingMember === member.user_id}
                              className="text-red-600 hover:text-red-900 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {removingMember === member.user_id ? 'Removing...' : 'Remove'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Invites List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Pending Invitations</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Manage team member invitations
          </p>
        </div>
        
        {invites.filter(invite => invite.status === 'pending').length === 0 && invites.filter(invite => invite.status === 'expired').length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No invitations</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by inviting a team member.</p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowInviteForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style={{ backgroundColor: primary }}
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Invite Team Member
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Pending Invites */}
            {invites.filter(invite => invite.status === 'pending').length > 0 && (
              <ul className="divide-y divide-gray-200">
                {invites.filter(invite => invite.status === 'pending').map((invite) => (
                  <li key={invite.id}>
                    <div className="px-4 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {invite.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{invite.email}</p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invite.status)}`}>
                              {invite.status}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <span className="capitalize">{invite.role}</span>
                            <span className="mx-2">•</span>
                            <span>Invited {formatDate(invite.created_at)}</span>
                            <span className="mx-2">•</span>
                            <span>Expires {formatDate(invite.expires_at)}</span>
                          </div>
                          {invite.message && (
                            <p className="text-sm text-gray-600 mt-1">{invite.message}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleResendInvite(invite.id)}
                          disabled={resendingInvite === invite.id}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {resendingInvite === invite.id ? 'Resending...' : 'Resend'}
                        </button>
                        <button
                          onClick={() => handleRevokeInvite(invite.id)}
                          disabled={revokingInvite === invite.id}
                          className="text-red-600 hover:text-red-900 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {revokingInvite === invite.id ? 'Revoking...' : 'Revoke'}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            
            {/* Expired Invites */}
            {invites.filter(invite => invite.status === 'expired').length > 0 && (
              <div className="border-t border-gray-200">
                <div className="px-4 py-3 bg-red-50">
                  <h4 className="text-sm font-medium text-red-900">Expired Invitations</h4>
                  <p className="text-xs text-red-700 mt-1">These invitations have expired and can no longer be accepted.</p>
                </div>
                <ul className="divide-y divide-gray-200">
                  {invites.filter(invite => invite.status === 'expired').map((invite) => (
                    <li key={invite.id} className="opacity-60">
                      <div className="px-4 py-4 flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {invite.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">{invite.email}</p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invite.status)}`}>
                                {invite.status}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <span className="capitalize">{invite.role}</span>
                              <span className="mx-2">•</span>
                              <span>Invited {formatDate(invite.created_at)}</span>
                              <span className="mx-2">•</span>
                              <span className="text-red-600">Expired {formatDate(invite.expires_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </TooltipProvider>
  );
};
